import { React, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loggedIn } from '../../state/loginSlice';
import { useSearchParams } from 'react-router-dom';

import { Typography, Card, CircularProgress, Snackbar, Alert, Link, Stack, IconButton, Tooltip } from '@mui/material';
import ActionButton from '../../lib/ActionButton';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import PropTypes from 'prop-types';

import { useGetDatasetsQuery } from '../../state/apiSlice';
import Code from '../codeHighlighting';
import Foldable from '../foldable';

// Reuse JupyterLink's card styling so the two tabs look identical.
import styles from './JupyterLink.module.css';

const apiUrl = window._env_.REACT_APP_WCIBAPI_URL || '';
// Optional fallback — authoritative dashboard URL comes back in the
// /v1/argo/provision response. Set this only if you want to render the
// "Open Argo Dashboard" link before the user has clicked once.
const argoDashboardFallback = window._env_.REACT_APP_ARGO_DASHBOARD_URL || '';

// Argo install namespace — drives every user-visible namespace string in
// the docs samples below. The shape is:
//   <argoInstallNamespace>             — Argo control-plane install ns
//   <argoInstallNamespace>-jobs-<user> — per-tenant workflows ns
//   <argoInstallNamespace>-events      — shared platform Argo Events ns
// Same ConfigMap-driven values that wcibapi's provisioner reads from
// argo-batch-provisioner-config; the chart publishes the install ns
// here so docs in staging vs prod show the right paths. Defaults to
// 'argo-batch-staging' for older charts that don't publish the var.
const argoInstallNamespace = window._env_.REACT_APP_ARGO_INSTALL_NAMESPACE || 'argo-batch-staging';
const tenantNsPrefix = `${argoInstallNamespace}-jobs-`;
const eventsNamespace = `${argoInstallNamespace}-events`;

// Render the user's actual signum into samples when known (the loginSlice
// default 'unknown' or any falsy/empty string falls back to a generic
// placeholder so the snippet still parses as documentation when called
// from a logged-out state). Most consumers below are functions that take
// a username arg explicitly so they can be statically called from JSX.
const userOrPlaceholder = (u) => (u && u !== 'unknown') ? u : '<your-username>';

// ---------------------------------------------------------------------------
// Code samples — driven from the user's selected dataset where possible so
// they're copy-pasteable rather than abstract. Comments are densest in
// sampleAnalyzeWorkflow (the first sample); subsequent samples don't
// repeat field-level explanations.
// ---------------------------------------------------------------------------

/**
 * Pure-Python smoke test — list every file in `ds`, run from a Jupyter
 * cell. Same API as the Argo workflow below; included here so users see
 * "in a notebook" and "in a workflow pod" are the same code, just
 * different host environments.
 *
 * @param {{DatasetID: number, DatasetName: string}} ds
 * @returns {string} Python source as a single string for the rendered code block.
 */
const sampleListFiles = (ds) => `from dataportal import DataportalClient

# Token: portal frontend → Jupyterhub tab → "Generate Token". Same JWT
# a notebook uses; short-lived; do NOT commit. JupyterHub already sets
# DATAPORTAL_CLIENT_TOKEN; locally, export it in your shell.
import os
token = os.environ.get('DATAPORTAL_CLIENT_TOKEN', '')

client = DataportalClient(token)
# fromDataset() takes a name (str) or DatasetID (int).
# listFiles() yields {"FileID": int, "MFileName": str, "FileSize": int, ...}
for datafile in client.fromDataset('${ds.DatasetName}').listFiles():
    print(datafile)
`;

/**
 * Minimal "list every file in a dataset" Argo Workflow.
 *
 * Single-template (`analyze`) workflow that runs one pod which:
 *   1. pip-installs `dataportalclient` from GitHub,
 *   2. heredoc-pipes a Python script into `python -`,
 *   3. iterates `client.fromDataset(...).listFiles()` and prints one
 *      line per file (FileID, MFileName, FileSize).
 *
 * The "Hello, World" of Argo on WARA-Ops — minimal but runnable. Field
 * comments here are the verbose set; subsequent samples don't repeat
 * them.
 *
 * @param {{DatasetID: number, DatasetName: string}} ds
 * @returns {string} A complete Workflow YAML, dataset name interpolated.
 */
const sampleAnalyzeWorkflow = (ds, username) => `# Minimal Workflow: one pod lists every file in dataset
# "${ds.DatasetName}" and prints (FileID, MFileName, FileSize).
# Inspect: argo logs -n ${tenantNsPrefix}${userOrPlaceholder(username)} @latest

apiVersion: argoproj.io/v1alpha1   # Argo Workflows CRD group.
kind: Workflow                     # Runs once on submit.

metadata:
  # Argo appends a 5-char random suffix; resubmit-safe.
  generateName: analyze-${ds.DatasetName.toLowerCase().replace(/[^a-z0-9-]/g, '-')}-
  # controller-instanceid: required label that routes this Workflow CR to
  # the env's argo-workflows controller. The cluster runs one controller
  # per env (argo-batch-staging + argo-batch-prod side-by-side); each
  # filters to Workflows whose label matches its instanceID. Without
  # this, the Workflow sits Pending forever — no controller picks it up.
  # Value MUST equal '${argoInstallNamespace}' for this env.
  labels:
    workflows.argoproj.io/controller-instanceid: ${argoInstallNamespace}

spec:
  entrypoint: analyze              # First template to run.
  serviceAccountName: argo-batch-executor   # Pre-provisioned tenant SA.

  # podMetadata is applied to every pod the workflow spawns. The
  # 'argo-batch.wcib/internet-egress: "true"' label opts pods into
  # internet egress: tenant default-egress NetworkPolicy is default-deny
  # outbound, and a second NetworkPolicy (internet-egress-opt-in) selects
  # on this label to allow it. Without it, 'pip install ...' times out.
  # WCIB API + DNS are reachable regardless. (Subsequent samples on this
  # page set the same label without re-explaining it.)
  podMetadata:
    labels:
      argo-batch.wcib/internet-egress: "true"

  # Template kinds: container | script | dag | steps | resource.
  templates:
    - name: analyze
      container:
        image: python:3.13
        command: [bash, -lc]       # bash login + run command.
        args:
          - |
            pip install git+https://github.com/wara-ops/DataportalClient.git
            # 'PY' single-quoted: no shell expansion inside the heredoc.
            python - <<'PY'
            from dataportal import DataportalClient
            # No-arg constructor: reads DATAPORTAL_CLIENT_TOKEN from
            # env: below, plus DATAPORTAL_API and DATAPORTAL_CLIENT_BASEPATH
            # which argo-batch auto-injects (cache → /tmp/.cache/portalclient).
            client = DataportalClient()
            for f in client.fromDataset("${ds.DatasetName}").listFiles():
                print(f["FileID"], f["MFileName"], f["FileSize"])
            PY
        env:
          # Token: portal frontend → Jupyterhub tab → "Generate Token".
          # Short-lived; do NOT commit. Audit log: tokenName=<from-token>,
          # user=${userOrPlaceholder(username)}.
          - name: DATAPORTAL_CLIENT_TOKEN
            value: "<paste-token-from-portal-Generate-Token>"

        # Kueue admits on requests; limits cap blast radius.
        # cpu: "500m" = 0.5 cores, "2" = 2 cores; memory "1Gi" = 1 GiB.
        resources:
          requests: { cpu: "500m", memory: "1Gi" }
          limits:   { cpu: "2",    memory: "4Gi" }
`;

/**
 * Fan-out / fan-in pipeline. Three templates orchestrated by a DAG:
 *   1. `pick-files` — list dataset, take the first N file ids, emit them
 *      as a JSON array on stdout. Argo captures stdout-via-file as an
 *      output parameter.
 *   2. `count-one`  — runs ONCE PER element of pick-files's output
 *      array, in parallel pods (Argo's `withParam`). Each pod streams
 *      its file via `dataportalclient.getData(generator=True)` and
 *      counts rows.
 *   3. `reduce`     — receives all per-file results aggregated into a
 *      single JSON array (Argo's per-task aggregate), writes a CSV
 *      summary.
 *
 * The CSV is exposed as an output artifact so the dashboard offers it
 * as a download link from the run details page.
 *
 * @param {{DatasetID: number, DatasetName: string}} ds
 * @returns {string} A complete Workflow YAML with three templates + DAG.
 */
const sampleProcessWorkflow = (ds) => `# Fan-out / fan-in: count rows in the first N files of dataset
# "${ds.DatasetName}" in parallel, then reduce per-file counts into one
# CSV. Argo materialises one pod per item, Kueue admits as quota
# permits, the rest queue.
#
# DAG shape:
#                       pick-files
#                            |
#                  +---------+---------+--- ... ---+
#                  v         v         v           v
#              count-one  count-one  count-one   count-one    (parallel)
#                  |         |         |           |
#                  +---------+---------+--- ... ---+
#                            |
#                          reduce  (aggregates results, writes summary.csv)

apiVersion: argoproj.io/v1alpha1
kind: Workflow

metadata:
  generateName: count-${ds.DatasetName.toLowerCase().replace(/[^a-z0-9-]/g, '-')}-
  # See the analyze sample above for why this label is required.
  labels:
    workflows.argoproj.io/controller-instanceid: ${argoInstallNamespace}

spec:
  entrypoint: count-pipeline       # The DAG, defined at the bottom.
  serviceAccountName: argo-batch-executor

  podMetadata:
    labels:
      argo-batch.wcib/internet-egress: "true"

  # Workflow inputs; reference as {{workflow.parameters.X}}. Override at
  # submit:  argo submit -p limit=50 process.yaml
  arguments:
    parameters:
      - { name: dataset_name, value: "${ds.DatasetName}" }
      - { name: limit,        value: "10" }   # fan out across the first 10 files

  templates:

    # ---- Step 1: list dataset, pick first N file IDs --------------------
    - name: pick-files
      # Step inputs (filled by the DAG below).
      inputs:
        parameters:
          - { name: dataset_name }
          - { name: limit }
      # Capture file content as the output value (Argo reads /tmp/files.json
      # at step end; downstream tasks see it as 'files').
      outputs:
        parameters:
          - name: files
            valueFrom: { path: /tmp/files.json }
      container:
        image: python:3.13
        command: [bash, -lc]
        args:
          - |
            pip install --quiet git+https://github.com/wara-ops/DataportalClient.git
            python - <<'PY' > /tmp/files.json
            import os, json
            from dataportal import DataportalClient

            client = DataportalClient()
            limit = int(os.environ["LIMIT"])
            files = []
            # Break locally — listFiles() iterates the whole dataset.
            for f in client.fromDataset(os.environ["DATASET_NAME"]).listFiles():
                files.append({"id": f["FileID"], "name": f["MFileName"]})
                if len(files) >= limit:
                    break
            print(json.dumps(files))
            PY
        env:
          - { name: DATASET_NAME, value: "{{inputs.parameters.dataset_name}}" }
          - { name: LIMIT,        value: "{{inputs.parameters.limit}}" }
          - name: DATAPORTAL_CLIENT_TOKEN
            value: "<paste-token-from-portal-Generate-Token>"

    # ---- Step 2: count rows in ONE file (one pod per file) --------------
    # getData(generator=True) yields pandas DataFrames; sum len(chunk)
    # without holding the whole file in memory.
    - name: count-one
      inputs:
        parameters:
          - { name: dataset_name }
          - { name: file_id }
          - { name: file_name }
      # One JSON object per pod; the reduce step receives all of them
      # aggregated as a JSON array (see DAG section).
      outputs:
        parameters:
          - name: result
            valueFrom: { path: /tmp/result.json }
      container:
        image: python:3.13
        command: [bash, -lc]
        args:
          - |
            pip install --quiet git+https://github.com/wara-ops/DataportalClient.git
            python - <<'PY' > /tmp/result.json
            import os, json
            from dataportal import DataportalClient

            client = DataportalClient()
            client.fromDataset(os.environ["DATASET_NAME"])
            file_id = int(os.environ["FILE_ID"])

            # entries=50000 → 50k-row chunks. entries=-1 reads the whole
            # file in one DataFrame (fine for small, dangerous for large).
            rows = 0
            for chunk in client.getData(fileID=file_id, entries=50000, generator=True):
                rows += len(chunk)

            print(json.dumps({
                "file_id":   file_id,
                "file_name": os.environ["FILE_NAME"],
                "lines":     rows,
            }))
            PY
        env:
          # workflow.parameters → all pods see the same value;
          # inputs.parameters → fan-out fills these per pod.
          - { name: DATASET_NAME, value: "{{workflow.parameters.dataset_name}}" }
          - { name: FILE_ID,      value: "{{inputs.parameters.file_id}}" }
          - { name: FILE_NAME,    value: "{{inputs.parameters.file_name}}" }
          - name: DATAPORTAL_CLIENT_TOKEN
            value: "<paste-token-from-portal-Generate-Token>"
        resources:
          requests: { cpu: "200m", memory: "256Mi" }
          limits:   { cpu: "1",    memory: "1Gi" }

    # ---- Step 3: reduce per-file results into one CSV -------------------
    # Receives the aggregated JSON array (see DAG below), writes a CSV.
    - name: reduce
      inputs:
        parameters:
          - { name: results }   # JSON-encoded array of count-one outputs
      # Files (vs parameters which are inline strings ≤ ~256 KiB):
      # uploaded to the cluster artifact store, downloadable from the
      # dashboard's Artifacts tab. Write under /tmp because that's the
      # only writable path PSS-restricted tenant pods get out of the
      # box (UID 8737, no chart-provided emptyDir, root FS read-only
      # by convention).
      outputs:
        artifacts:
          - { name: summary, path: /tmp/summary.csv }
      container:
        image: python:3.13
        command: [bash, -lc]
        args:
          - |
            python - <<'PY'
            import json, os, csv
            results = json.loads(os.environ["RESULTS"])
            total = sum(r["lines"] for r in results)
            with open("/tmp/summary.csv", "w") as f:
                w = csv.writer(f)
                w.writerow(["file_id", "file_name", "lines"])
                for r in sorted(results, key=lambda r: r["file_id"]):
                    w.writerow([r["file_id"], r["file_name"], r["lines"]])
                w.writerow([])
                w.writerow(["total_files", "total_lines"])
                w.writerow([len(results), total])
            print(f"counted {len(results)} files, {total:,} total lines")
            PY
        env:
          - { name: RESULTS, value: "{{inputs.parameters.results}}" }

    # ---- DAG: pick -> fan-out (count-one per file) -> reduce ------------
    # Tasks reference templates above by name; depends/withParam wire
    # them into a graph.
    - name: count-pipeline
      dag:
        tasks:

          - name: pick                      # Root of the DAG (no depends).
            template: pick-files
            arguments:
              parameters:
                - { name: dataset_name, value: "{{workflow.parameters.dataset_name}}" }
                - { name: limit,        value: "{{workflow.parameters.limit}}" }

          - name: count
            template: count-one
            depends: pick                   # Wait for pick to succeed.
            # Fan out: one task per JSON-array element. Argo creates
            # count(0), count(1), …, count(N-1) as separate pods,
            # parallel up to spec.parallelism (uncapped here, but
            # Kueue's LocalQueue still gates by tenant quota). Element
            # fields accessible as {{item.id}} / {{item.name}}.
            withParam: "{{tasks.pick.outputs.parameters.files}}"
            arguments:
              parameters:
                - { name: dataset_name, value: "{{workflow.parameters.dataset_name}}" }
                - { name: file_id,      value: "{{item.id}}" }
                - { name: file_name,    value: "{{item.name}}" }

          - name: reduce
            template: reduce
            depends: count
            arguments:
              parameters:
                # Per-task AGGREGATE: when withParam fanned out, each
                # child's outputs.parameters.<X> is collected into a
                # single JSON array, addressable here as
                # {{tasks.count.outputs.parameters.result}}. Arrives at
                # reduce as e.g. '[{...}, {...}, {...}]'.
                - { name: results, value: "{{tasks.count.outputs.parameters.result}}" }
`;

/**
 * Minimal CronWorkflow — runs once a week. The body inside `workflowSpec`
 * is a normal Workflow spec; on every cron tick, Argo creates a fresh
 * Workflow from it. Useful for nightly aggregations, weekly reports,
 * resource-usage summaries — anything that should run on a schedule
 * without you clicking submit.
 */
const sampleCronWorkflow = `# CronWorkflow: on every cron tick the controller creates a fresh
# Workflow from the embedded workflowSpec, runs it, and waits for the
# next tick.

apiVersion: argoproj.io/v1alpha1
kind: CronWorkflow                # Schedules a Workflow on a cron tick.

metadata:
  # Singleton — use 'name' (not generateName) so 'kubectl apply'
  # updates the existing schedule rather than creating duplicates.
  name: weekly-report
  # controller-instanceid label routes this CronWorkflow to the env's
  # argo-workflows controller (see sampleAnalyzeWorkflow comment). The
  # spec.workflowMetadata.labels block below is ALSO required: spawned
  # Workflows do not auto-inherit labels from the CronWorkflow, and
  # without the label they're orphaned by the controller too.
  labels:
    workflows.argoproj.io/controller-instanceid: ${argoInstallNamespace}

spec:
  # cron 5-field: "min hour day-of-month month day-of-week".
  # "0 9 * * 1" → every Monday 09:00.
  schedule: "0 9 * * 1"
  # IANA zone name; defaults to UTC if omitted.
  timezone: Europe/Stockholm

  # workflowMetadata: copied onto every Workflow CR this CronWorkflow
  # spawns on each tick. Mirror controller-instanceid here too.
  workflowMetadata:
    labels:
      workflows.argoproj.io/controller-instanceid: ${argoInstallNamespace}

  # workflowSpec: a full Workflow spec (templates, arguments,
  # podMetadata, …) — copied into a new Workflow on every tick.
  workflowSpec:
    entrypoint: report
    serviceAccountName: argo-batch-executor
    templates:
      - name: report
        container:
          # ':latest' means "whatever's tagged latest at pod start".
          # In production, pin to a digest or version tag.
          image: your-registry/weekly-report:latest
          env:
            # If your schedule outlives the token TTL, store the token
            # as a long-lived Secret and use valueFrom.secretKeyRef.
            - name: DATAPORTAL_CLIENT_TOKEN
              value: "<paste-token-from-portal-Generate-Token>"
`;

/**
 * Argo Events Sensor that submits a Workflow into a tenant namespace
 * every time the WCIB MQTT ingester fires a 'batch-uploaded' webhook.
 *
 * Lives in the platform-only events namespace ('<argo install
 * namespace>-events', e.g. argo-batch-staging-events on staging),
 * NOT the user's tenant namespace — the Sensor's ServiceAccount has
 * cluster-wide permission to create Workflows in any tenant
 * namespace, which is privileged enough that we don't expose it to
 * users.
 *
 * Shown here so the routing contract (which event field maps to which
 * Workflow parameter / metadata field) is visible to users designing
 * their own WorkflowTemplate. Users do NOT deploy this themselves.
 */
const sampleMqttSensor = `# (Platform-team only — lives in the shared '${eventsNamespace}' namespace.)
# Receives a webhook event from the WCIB ingester each time an MQTT
# batch threshold is hit, then submits a Workflow into the right tenant
# namespace. parameters[].dataKey bindings (further down) splice fields
# from the webhook body into the Workflow before submission.

apiVersion: argoproj.io/v1alpha1   # Same group as Workflows; different controller.
kind: Sensor                       # Argo Events: submits Workflows on external events.

metadata:
  name: mqtt-batch-trigger
  # Lives in the platform namespace, NOT a tenant namespace — the
  # Sensor's SA has cluster-wide 'create workflows' permission.
  namespace: ${eventsNamespace}

spec:
  # dependencies: bind (EventSource, eventName) pairs that fire this Sensor.
  dependencies:
    - name: mqtt-batch
      # 'wcib-ingester' is a webhook-type EventSource (TCP 12000).
      eventSourceName: wcib-ingester
      eventName: batch-uploaded

      # Body-shape filter only. Filter types: data | exprs | time | ctx.
      # HMAC verification lives on the EventSource (webhook.authSecret),
      # not here — see argo-events docs for the webhook source.
      filters:
        data:
          - path: body.event
            type: string
            value: ["batch-uploaded"]

  # triggers: what to do when a dependency fires.
  triggers:
    - template:
        name: submit-analyze
        # Trigger kinds: argoWorkflow | http | slack | awsLambda | …
        argoWorkflow:
          # operation: submit | submitFromTemplate | resubmit | update | patch
          operation: submit
          source:
            resource:
              apiVersion: argoproj.io/v1alpha1
              kind: Workflow
              metadata:
                generateName: mqtt-batch-

                # Sentinel — overwritten at submit time by the
                # parameters[] entry binding body.tenantNamespace below.
                # We use a non-namespace-shaped string so a missed
                # binding fails fast (k8s rejects 'PLACEHOLDER-…') rather
                # than silently submitting into the wrong namespace.
                namespace: PLACEHOLDER-OVERWRITTEN-AT-SUBMIT

                # controller-instanceid: routes the spawned Workflow to
                # the env's argo-workflows controller. Hardcoded to the
                # Sensor's env ('${argoInstallNamespace}'); this Sensor
                # must therefore only route to tenant namespaces in the
                # same env. Cross-env routing requires a separate Sensor
                # in the other env's events namespace.
                labels:
                  workflows.argoproj.io/controller-instanceid: ${argoInstallNamespace}

              # User-side template name; the Sensor only routes parameters.
              spec:
                workflowTemplateRef: { name: on-mqtt-batch }
                arguments:
                  parameters:
                    # Initial values overwritten by parameters[] below.
                    # Argo validates that referenced params have a value
                    # at admit time, so non-empty placeholders are required.
                    - { name: dataset_id, value: "0" }
                    - { name: file_id,    value: "0" }
                    - { name: trigger,    value: "mqtt-batch" }

          # parameters[]: rewrite rules between source above and the
          # submitted Workflow.  src.dataKey = gjson path inside body;
          # dest = dotted path in the resource above; operation defaults
          # to overwrite (also: append | prepend).
          parameters:
            - src: { dependencyName: mqtt-batch, dataKey: body.datasetId }
              dest: spec.arguments.parameters.0.value
            - src: { dependencyName: mqtt-batch, dataKey: body.fileId }
              dest: spec.arguments.parameters.1.value

            - src:
                dependencyName: mqtt-batch
                # Pre-built full namespace from the webhook body (see
                # sampleIngesterWebhook). Done payload-side because
                # Argo Events dataKey paths read JSON, can't transform.
                dataKey: body.tenantNamespace
              dest: metadata.namespace
              operation: overwrite          # Replace the placeholder.
`;

/**
 * User-side reusable WorkflowTemplate. Fired by the platform Sensor
 * (sampleMqttSensor above) once per MQTT batch.
 *
 * The template lives in the user's tenant namespace and the Sensor
 * references it by name (workflowTemplateRef.name = on-mqtt-batch),
 * so the user owns the processing logic and the platform owns only
 * the routing.
 *
 * Same shape as a Workflow's spec, just under `kind: WorkflowTemplate`
 * — Argo recognises the template kind and stores it for later
 * instantiation rather than running it immediately.
 */
const sampleMqttWorkflowTemplate = `# Reusable WorkflowTemplate fired once per MQTT batch by the platform's
# ${eventsNamespace} Sensor. Lives in YOUR tenant namespace; you own
# the processing logic, the platform owns only the routing.
#
# Apply once: kubectl -n <tenant-ns> apply -f on-mqtt-batch.yaml
# Update:     re-apply; the next Sensor trigger uses the new version.

apiVersion: argoproj.io/v1alpha1
kind: WorkflowTemplate             # Reusable; instantiated by name (no auto-run).

metadata:
  # Sensor references this by (namespace, name) via workflowTemplateRef.
  name: on-mqtt-batch
  # (Implicit) namespace: from 'kubectl -n <your-tenant-ns>'.

spec:
  entrypoint: process
  serviceAccountName: argo-batch-executor

  podMetadata:
    labels:
      argo-batch.wcib/internet-egress: "true"

  # Empty defaults — required, supplied by the Sensor at submit time.
  # Empty values fail fast if the Sensor forgets a key.
  arguments:
    parameters:
      - { name: dataset_id, value: "" }
      - { name: file_id,    value: "" }
      - { name: trigger,    value: "" }

  templates:
    - name: process
      inputs:
        parameters:
          - { name: dataset_id }
          - { name: file_id }
          - { name: trigger }
      container:
        image: python:3.13
        command: [bash, -lc]
        args:
          - |
            pip install --quiet git+https://github.com/wara-ops/DataportalClient.git
            python - <<'PY'
            import os
            from dataportal import DataportalClient
            # Authorises as YOU — the ingester never forwards its own
            # credentials. Audit log: tokenName=argo-job-..., user=<you>.
            client = DataportalClient()
            client.fromDataset(int(os.environ["DATASET_ID"]))
            file_id = int(os.environ["FILE_ID"])

            # Same chunk-stream idiom as count-one above.
            rows = 0
            for chunk in client.getData(fileID=file_id, entries=50000, generator=True):
                rows += len(chunk)

            print(f"trigger={os.environ['TRIGGER']} dataset={os.environ['DATASET_ID']} "
                  f"file={file_id} rows={rows}")
            PY
        env:
          - { name: DATASET_ID, value: "{{inputs.parameters.dataset_id}}" }
          - { name: FILE_ID,    value: "{{inputs.parameters.file_id}}" }
          - { name: TRIGGER,    value: "{{inputs.parameters.trigger}}" }
          - name: DATAPORTAL_CLIENT_TOKEN
            value: "<paste-token-from-portal-Generate-Token>"
        resources:
          requests: { cpu: "200m", memory: "256Mi" }
          limits:   { cpu: "1",    memory: "1Gi" }
`;

/**
 * Platform-side ingester webhook. Lives at
 * `wcibapi/ingester/mqtt_ingester.py` in the WCIB monorepo and runs
 * inside the platform's namespace (frontend/wcibapi pod), NOT in any
 * user's tenant namespace.
 *
 * Shown to users as the contract for the event payload — the keys on
 * the JSON body here are exactly what `sampleMqttSensor`'s
 * `parameters[].dataKey` bindings reference (body.event, body.datasetId,
 * body.fileId, body.user, body.timestamp).
 *
 * Illustrative pseudo-code — `log` is referenced but not imported in
 * the snippet, mirroring the real file's logging-already-in-scope
 * assumption. Don't copy this into a fresh script and expect it to run
 * as-is.
 */
const sampleIngesterWebhook = `# wcibapi/ingester/mqtt_ingester.py — runs platform-side, after a batch
# upload completes. Users do NOT deploy this; shown here so the event
# payload contract is explicit.
import os, time, hmac, hashlib, json, requests

ARGO_WEBHOOK = os.getenv("ARGO_EVENTS_WEBHOOK_URL")  # e.g. http://...:12000/batch-uploaded

# Shared HMAC secret — the matching half is mounted into the Argo Events
# EventSource pod (webhook.authSecret), which verifies every X-WCIB-
# Signature header against it.
SHARED_SECRET = os.environ["ARGO_EVENTS_HMAC_SECRET"].encode()

def notify_argo(dataset_id: int, file_id: int, user: str) -> None:
    if not ARGO_WEBHOOK:
        # No webhook configured — silent no-op so MQTT ingestion still
        # works in environments without Argo Events deployed.
        return
    body = json.dumps({
        "event": "batch-uploaded",
        "datasetId": dataset_id,
        "fileId":    file_id,
        "user":      user,
        # Pre-built so the Sensor can splice it as one field
        # (Argo Events dataKey can't string-concat).
        "tenantNamespace": f"${tenantNsPrefix}{user}",
        "timestamp": int(time.time()),
    }).encode()
    sig = hmac.new(SHARED_SECRET, body, hashlib.sha256).hexdigest()
    try:
        requests.post(
            ARGO_WEBHOOK,
            data=body,
            headers={
                "Content-Type":     "application/json",
                "X-WCIB-Signature": f"sha256={sig}",
            },
            timeout=2.0,                    # never block ingestion
        )
    except Exception as e:
        # Argo Events being down must NOT break MQTT ingestion.
        log.warning("argo-events webhook failed: %s", e)
`;

/**
 * The three argo-CLI commands every new user runs in their first
 * session: submit, list, logs. Note: the argo CLI uses its own auth
 * (token, kubeconfig, or 'argo auth login --auth-mode=client'), NOT
 * the browser SSO session — install the CLI locally and authenticate
 * separately the first time.
 *
 * @param {string} username — used to splice the tenant namespace into
 *   the commands so users can copy-paste verbatim. Falls back to
 *   '<your-username>' when not logged in.
 */
const submitFromCli = (username) => {
    const u = userOrPlaceholder(username);
    return `# Save the workflow above to analyze.yaml, then submit. Tenant ns
# is ${tenantNsPrefix}${u} (your WCIB username spliced in by the
# portal — replace the namespace if you copy this from a doc page).
argo submit analyze.yaml \\
    -n ${tenantNsPrefix}${u}

# List your workflows (newest first; status, age, duration):
argo list -n ${tenantNsPrefix}${u}

# Stream logs of the most recent run (@latest = highest creationTimestamp):
argo logs -n ${tenantNsPrefix}${u} @latest
`;
};

// ---------------------------------------------------------------------------

/**
 * Small inline component: renders "Transaction ID: <txid>" with a copy
 * button. Used inside Alerts that surface a structured backend failure
 * carrying a requestId so the user can paste it cleanly to support.
 *
 * The Tooltip swaps from "Copy" to "Copied!" for ~1.5 s after click,
 * giving immediate feedback without an extra Snackbar. navigator.clipboard
 * is used; falls back silently if unavailable (older browsers, insecure
 * contexts) — the txid is also rendered as selectable <code> text so
 * the user can manually copy regardless.
 */
/**
 * Inline abbreviation with a tooltip showing the expansion. Hover/tap
 * reveals the full term; visually we underline-on-dot the abbreviation
 * so users notice it's interactive without making the whole paragraph
 * busy. Use sparingly — only for terms users may not know.
 */
function Abbr({ title, children }) {
    return (
        <Tooltip title={title} placement="top" arrow enterTouchDelay={0}>
            <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>
                {children}
            </span>
        </Tooltip>
    );
}

function TxIdLine({ txid }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(txid).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
            }).catch(() => {});
        }
    };
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
            Transaction ID:{' '}
            <code style={{
                padding: '0.1rem 0.4rem',
                fontSize: '0.85em',
                backgroundColor: 'rgb(0 0 0 / 6%)',
                borderRadius: 3,
                userSelect: 'all',
            }}>{txid}</code>
            <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
                <IconButton
                    size="small"
                    onClick={handleCopy}
                    aria-label="Copy transaction ID to clipboard"
                    sx={{ p: 0.25 }}
                >
                    <ContentCopyIcon fontSize="inherit" sx={{ fontSize: '1rem' }} />
                </IconButton>
            </Tooltip>
        </span>
    );
}

TxIdLine.propTypes = {
    txid: PropTypes.string.isRequired,
};

Abbr.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

export default function MyJobs() {
    const { data: datasets, isSuccess } = useGetDatasetsQuery();
    const selectedID = useSelector(state => state.dataset.selectedID);
    const isLoggedIn = useSelector(state => state.login.value);
    const username = useSelector(state => state.login.user?.username || 'unknown');
    const dispatch = useDispatch();

    const [statusLoading, setStatusLoading] = useState(true);
    const [provisioned, setProvisioned] = useState(false);
    const [provisioning, setProvisioning] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    // Structured S3-provision failure surfaced from a same-tab
    // openMyJobs() click. Held in dedicated state (not errorMsg) because
    // the user must be able to copy the txid — the auto-dismissing
    // Snackbar isn't appropriate. Cleared by the user via the Alert's
    // close button or implicitly by a successful retry.
    const [s3FailureTxid, setS3FailureTxid] = useState(null);

    // Surface ?argosetup=error placed by /v1/oidc/auth (or by
    // LoginDialog.provisionThenRedirect) when its provisioning step
    // fails. The user lands here instead of being dumped in a broken
    // Argo dashboard. We strip the params after the user dismisses the
    // alert so they don't persist on refresh.
    //
    // Optional companion params:
    //   reason=s3   — MinIO/S3 artifact-storage provisioning failed
    //                 (vs the generic auto-provision bounce).
    //   txid=<uuid> — backend requestId; rendered as a copyable
    //                 transaction ID for support hand-off.
    const [searchParams, setSearchParams] = useSearchParams();
    const argoSetupError = searchParams.get('argosetup') === 'error';
    const argoSetupReason = searchParams.get('reason');
    const argoSetupTxid = searchParams.get('txid');

    useEffect(() => {
        let cancelled = false;
        if (!isLoggedIn) {
            setStatusLoading(false);
            return undefined;
        }
        (async () => {
            try {
                const r = await axios.get(`${apiUrl}/v1/argo/status`, { withCredentials: true });
                if (cancelled) return;
                setProvisioned(!!r.data.provisioned);
                // Hydrate Redux's loginSlice when it's still at the initial
                // (logged-out) state. The portal's LoginDialog dispatches on
                // a fresh login, but a session-cookie-restored user (e.g.
                // an OIDC-SSO bounce, or just a page reload) skips that path
                // and arrives here with state.login.user === null. Idempotent:
                // a no-op if Redux already has the right user — selector
                // returns 'unknown' only when state.login.user is null.
                //
                // /v1/argo/status returns just { provisioned, username }, not
                // the full /v1/login payload, so the dispatched user lacks
                // canCreateDataset/groups. stateTasks.checkLoginStatusAndRouteAsNeeded
                // calls /v1/validatesession on the next visibility tick and
                // backfills the rest. Wrapping the bare username in an object
                // here keeps loginSlice's contract (state.user is an object
                // or null, never a bare string).
                if (r.data.username && username === 'unknown') {
                    dispatch(loggedIn({ username: r.data.username }));
                }
            } catch (_err) {
                // 401 / network / endpoint missing — leave provisioned=false; the click
                // path retries against /provision and surfaces a real error if any.
            } finally {
                if (!cancelled) setStatusLoading(false);
            }
        })().catch(() => {});
        return () => { cancelled = true; };
    }, [isLoggedIn]);

    // Defensive reset: if the page re-renders with ?argosetup=error in the
    // URL, the user has just been bounced back here from a failed Argo
    // SSO setup. Whatever state the launch button was in before, it must
    // not look "in progress" anymore — the warning Alert above the button
    // is the truth, and a stuck spinner would be misleading.
    //
    // In practice the openMyJobs() handler clears `provisioning` in its
    // finally block, but the bounce-back to MyJobs originates from
    // wcibapi (full page reload, fresh component mount), so the
    // finally-clear never runs in that path. This effect guarantees
    // correctness regardless of how we arrived.
    useEffect(() => {
        if (argoSetupError && provisioning) {
            setProvisioning(false);
        }
    }, [argoSetupError, provisioning]);

    const openMyJobs = async () => {
        setProvisioning(true);
        setErrorMsg('');
        setS3FailureTxid(null);
        try {
            const response = await axios.post(`${apiUrl}/v1/argo/provision`, {}, { withCredentials: true });
            const { dashboardUrl } = response.data;
            const target = dashboardUrl || argoDashboardFallback;
            if (!target) {
                setErrorMsg('Argo dashboard URL is not configured.');
                return;
            }
            window.open(target, '_blank', 'noopener,noreferrer');
            setProvisioned(true);
        } catch (err) {
            // Structured 503 from wcibapi when MinIO/S3 artifact-storage
            // provisioning fails. Surface the requestId so the user can
            // hand it to support — auto-dismissing Snackbar would lose
            // it. Caught BEFORE the generic Snackbar fallback so the
            // user gets the actionable copy with the transaction ID.
            if (err.response?.status === 503 && err.response?.data?.error === 'argo-s3-provision-failed') {
                const requestId = err.response.data?.requestId || '';
                console.error('[MyJobs] argo-s3-provision-failed', {
                    requestId,
                    message: err.response.data?.message,
                });
                setS3FailureTxid(requestId);
                return;
            }
            const msg = err.response?.data || err.message || 'Failed to open Argo workspace';
            setErrorMsg(typeof msg === 'string' ? msg : JSON.stringify(msg));
        } finally {
            setProvisioning(false);
        }
    };

    let buttonLabel;
    if (provisioning) {
        buttonLabel = 'Opening Argo…';
    } else if (provisioned) {
        buttonLabel = 'To Argo!';
    } else {
        buttonLabel = 'Set up & open Argo';
    }

    // Rendered at the top AND bottom of the page so users don't have to
    // scroll past the (long) explainer to launch. Both renders share the
    // same handler/state, so clicking either one provisions+opens once.
    const renderArgoButton = (extraSx) => {
        if (!isLoggedIn) return null;
        if (statusLoading) return <CircularProgress sx={{ m: 2, ...extraSx }} />;
        return (
            <ActionButton
                onClick={openMyJobs}
                loading={provisioning}
                variant="outlined"
                size="large"
                /*
                 * Provisioning state visuals:
                 *   - ActionButton (project-wide LoadingButton wrapper)
                 *     defaults `loadingIndicator` to the WARA-Ops dotted-
                 *     square spinner and `loadingPosition` to "start", so
                 *     we don't need to pass either. Setting
                 *     `loading={provisioning}` flips the button to the
                 *     in-flight state: spinner anchored in the start slot
                 *     beside the label (never overlapping), button
                 *     auto-disabled, aria-busy set. ActionButton injects
                 *     a hidden placeholder startIcon so MUI Lab has
                 *     something to anchor "start" to even though we
                 *     don't pass a real startIcon — see ActionButton.js
                 *     for the rationale. No `disabled={provisioning}`
                 *     needed here.
                 *   - The spinner's dots use `currentcolor`, so they
                 *     inherit the Button's outlined-primary text colour
                 *     without any explicit prop.
                 *   - The endIcon stays as OpenInNew while idle and is
                 *     removed while loading so the button reads
                 *     single-purposedly as "working" rather than as a
                 *     navigation affordance mid-action.
                 */
                endIcon={provisioning ? null : <OpenInNewIcon />}
                sx={{ margin: 2, ...extraSx }}
                >
                {buttonLabel}
            </ActionButton>
        );
    };

    // Pick a dataset for the YAML example: prefer the user's selected one,
    // fall back to the first available, then to a clearly-invalid sentinel so
    // copy-pasted YAML fails loudly (404 from WCIB) rather than silently
    // hitting dataset ID 1 which may belong to another user.
    let ds = { DatasetID: 0, DatasetName: '<your-dataset>' };
    if (isSuccess && datasets?.filtered?.length) {
        ds = datasets.filtered.find(d => d.DatasetID === selectedID) || datasets.filtered[0];
    }
    const dsIsPlaceholder = ds.DatasetID === 0;

    return <>
        <Typography variant='h4' gutterBottom>
            Run batch workflows in Argo
        </Typography>

        {dsIsPlaceholder && isLoggedIn && (
            <Alert severity="info" sx={{ mb: 2 }}>
                Select a dataset above to populate the code samples with your dataset ID.
            </Alert>
        )}

        {argoSetupError && (
            <Alert
                /*
                 * severity escalates with reason: a generic auto-provision
                 * bounce (no reason) is a 'warning' (recoverable, click
                 * the button to retry); a structured S3 failure carries
                 * a transaction ID and is shown as 'error' so the visual
                 * weight matches the support hand-off the user is being
                 * asked to do.
                 */
                severity={argoSetupReason === 's3' ? 'error' : 'warning'}
                sx={{ mb: 2 }}
                onClose={() => {
                    // Clear the query params so the alert doesn't persist on
                    // refresh / back-nav. Keep any other params intact.
                    searchParams.delete('argosetup');
                    searchParams.delete('reason');
                    searchParams.delete('txid');
                    setSearchParams(searchParams, { replace: true });
                }}
                >
                {argoSetupReason === 's3' ? (
                    <>
                        Setting up your Argo workspace failed (artifact storage).
                        Please try again with <b>Set up &amp; open Argo</b> below.
                        If this persists, contact support
                        {argoSetupTxid && <> with <TxIdLine txid={argoSetupTxid} /></>}
                        {!argoSetupTxid && '.'}
                    </>
                ) : (
                    <>
                        We couldn&apos;t set up your Argo workspace automatically when you tried to sign in to Argo.
                        Click <b>Set up &amp; open Argo</b> below to retry. If the problem persists, contact support
                        {argoSetupTxid && <> with <TxIdLine txid={argoSetupTxid} /></>}
                        {!argoSetupTxid && '.'}
                    </>
                )}
            </Alert>
        )}

        {/*
          * Persistent error Alert for the in-tab openMyJobs() path when
          * wcibapi returns a structured 503 'argo-s3-provision-failed'.
          * Distinct from the generic Snackbar at the bottom (auto-
          * dismisses, no copy affordance) because the user MUST be able
          * to copy the transaction ID for support. Cleared by the user
          * via the Alert's close button or implicitly by a successful
          * retry (openMyJobs() resets s3FailureTxid at click).
          */}
        {s3FailureTxid !== null && (
            <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => setS3FailureTxid(null)}
                >
                Setting up your Argo workspace failed (artifact storage).
                Please try again. If this persists, contact support
                {s3FailureTxid
                    ? <> with <TxIdLine txid={s3FailureTxid} /></>
                    : '.'}
            </Alert>
        )}

        {/* Top-of-page launch button — same handler/state as the one at the
            bottom, so users don't have to scroll past the explainer to open
            Argo once they've read it the first time. */}
        {renderArgoButton({ mt: 0, mb: 2, ml: 0 })}

        <Card className={styles.expcard}>
            <Typography variant='body1' paragraph>
                <b>About:</b> WARA-Ops hosts a multi-tenant Argo Workflows instance in ERDC for batch and event-driven jobs against your datasets.
                Use the Argo button (above or at the end of this page) to open it — you will be automatically logged in with your current session.
                {' '}Each user gets an isolated Kubernetes namespace; no other user can see your workflows, logs, or artifacts. The namespace is created
                on your first visit and reused on subsequent ones.
            </Typography>

            <Typography variant='body1' paragraph>
                <b>What you get:</b> a private namespace (<code>{tenantNsPrefix}{userOrPlaceholder(username)}</code>) with the Argo Workflows + Argo Events
                {' '}<Abbr title="Custom Resource Definitions">CRDs</Abbr> available, capped at 200 pods, 500 <code>Workflow</code> objects, 20 <code>CronWorkflow</code>s,
                64 CPU, 256 GiB memory, 8 GPUs and 100 GiB storage per tenant. Submission is mediated by Kueue with fair-share scheduling — you queue against the same
                shared GPU pool as everyone else. You define workflows as a <Abbr title="Directed Acyclic Graph">DAG</Abbr> in YAML, submit them from the dashboard
                or the <code>argo</code> <Abbr title="command-line interface">CLI</Abbr>, schedule them with <code>CronWorkflow</code>, or have them triggered by
                MQTT messages and dataset file uploads via Argo Events. Each step runs as a pod in your namespace; the WCIB API and <code>dataportalclient</code> are
                reachable from inside, exactly like in JupyterHub.
            </Typography>

            <Typography variant='h6' sx={{ mt: 3 }} gutterBottom>Tokens — strategy</Typography>
            <Typography variant='body1' paragraph>
                The simplest pattern, shown in the examples below, is to paste a <b>dataset-scoped token</b> directly into your workflow YAML as
                an environment variable. Generate it in the portal frontend with <i>Generate Token</i> on the <i>Jupyterhub</i> tab — same
                {' '}<Abbr title="JSON Web Token">JWT</Abbr> a Jupyter notebook would use. Rotate when needed.
                <b> Do not commit tokens to git</b> — they are short-lived credentials.
                For repeated use, set the token as a JupyterHub environment variable and let your notebook substitute it into the workflow YAML
                before submitting.
            </Typography>
            <Typography variant='body1' paragraph>
                For high-security workloads use the <b>per-job minting</b> pattern: an init container exchanges a bootstrap token for a
                short-lived dataset-scoped token that dies with the workflow. Documented in
                {' '}the Argo design doc (see your platform team).
            </Typography>
            <Typography variant='body1' paragraph>
                Either way, the token is the same JWT a Jupyter notebook would use — the WCIB API audit log records every call with
                <code> tokenName</code>, <code>matchedScope</code>, and <code>user</code>, so workflow access is fully traceable.
            </Typography>

            <Typography variant='h6' sx={{ mt: 3 }} gutterBottom>Reading data from the portal</Typography>
            <Typography variant='body1' paragraph>
                Workflow pods reach the dataportal exactly like a Jupyter notebook does — same
                <code> dataportalclient</code>, same <code>DataportalClient</code> class, same
                <code> fromDataset(...).listFiles()</code> call. The only difference is where the token comes from: instead of pasting it into a
                cell, you supply it as an environment variable in your workflow YAML. Generate the token in the portal frontend on the
                <i> Jupyterhub</i> tab using the <i>Generate Token</i> button, then paste it directly into your workflow spec:
            </Typography>
            <Code language='yaml' code={`spec:
  templates:
    - name: main
      container:
        env:
          # Generated in the portal frontend: Jupyterhub tab -> "Generate Token".
          # Same JWT a Jupyter notebook would use. Short-lived; do NOT commit to git.
          - name: DATAPORTAL_CLIENT_TOKEN
            value: "<paste-token-from-portal-Generate-Token>"
`} />
            <Typography variant='body2' color='text.secondary' paragraph sx={{ mt: 1 }}>
                <b>Important:</b> tokens are short-lived — regenerate when needed. Do <b>not</b> commit tokens to git.
                For repeated use, set the token as a JupyterHub environment variable and let your notebook substitute it
                into the workflow YAML before submitting. The WCIB API audit log records every call with
                <code> tokenName</code>, <code>matchedScope</code>, and <code>user</code>, so workflow access is fully traceable.
            </Typography>
            <Typography variant='body2' color='text.secondary' paragraph>
                <b>DataportalClient API quick reference</b> (used in every Python sample on this page):
                <ul style={{ marginTop: 4, marginBottom: 4 }}>
                    <li><code>DataportalClient()</code> — no args; reads <code>DATAPORTAL_CLIENT_TOKEN</code>,
                        <code> DATAPORTAL_API</code> and <code>DATAPORTAL_CLIENT_BASEPATH</code> from the pod env.
                        The argo-batch chart pre-injects the latter two into every tenant pod, so the user only needs
                        to set the token. The cache lives at <code>$DATAPORTAL_CLIENT_BASEPATH/.cache/portalclient</code>
                        (= <code>/tmp/.cache/portalclient</code> in tenant pods).</li>
                    <li><code>client.fromDataset(name_or_id)</code> — selects a dataset; accepts the dataset name (string)
                        OR its numeric DatasetID (int). Returns the client, so calls chain.</li>
                    <li><code>client.listFiles()</code> — yields one dict per file:
                        <code> {`{"FileID": int, "MFileName": str, "FileSize": int, ...}`}</code>.
                        Iterates the entire dataset; cap with <code>itertools.islice</code> or a manual <code>break</code>.</li>
                    <li><code>client.getData(fileID, entries=N, generator=True)</code> — streams the file as a generator
                        of pandas DataFrames, each containing <code>N</code> rows. Use this for large files —
                        <code> entries=-1</code> reads the whole file in one DataFrame (fine for small files, dangerous
                        for large ones). <code>len(chunk)</code> on each yielded DataFrame gives a row count without
                        materialising the data.</li>
                </ul>
            </Typography>
            <Typography variant='body1' paragraph sx={{ mt: 2 }}>
                The Python you&apos;d run in a Jupyter cell to list the files in
                {!dsIsPlaceholder ? <> dataset <i>{ds.DatasetName}</i></> : <> a dataset</>}:
            </Typography>
            <Code language='python' code={sampleListFiles(ds)} />
            <Typography variant='body1' paragraph sx={{ mt: 2 }}>
                The same code, running as an Argo <code>Workflow</code>. The Workflow is a single template
                (<code>analyze</code>) that spawns one pod, pip-installs <code>dataportalclient</code>, and runs the
                Python script via a <code>bash -c</code> heredoc. Every line of YAML is annotated below — read it
                top-to-bottom for a tour of the spec fields you&apos;ll see in every sample on this page:
            </Typography>
            <Code language='yaml' code={sampleAnalyzeWorkflow(ds, username)} />
            <Typography variant='body1' paragraph sx={{ mt: 2 }}>
                Save it to <code>analyze.yaml</code> and submit it via the dashboard, or via the <code>argo</code> CLI
                installed locally (the CLI uses its own auth — token, kubeconfig, or <code>argo auth login --auth-mode=client</code> —
                NOT the browser <Abbr title="single sign-on">SSO</Abbr> session, so authenticate it once after install):
            </Typography>
            <Code language='bash' code={submitFromCli(username)} />

            <Foldable header="Fan-out: process many files in parallel">
                <Typography variant='body1' paragraph>
                    The interesting case is processing every file in a dataset concurrently. The Argo idiom is <b>fan-out / fan-in</b> — one task lists
                    the work, a second task runs <i>once per item</i> in parallel pods, a third reduces the per-item results into a single artifact.
                    The pipeline below counts the number of CSV rows in each of the first 10 files of
                    {!dsIsPlaceholder ? <> dataset <i>{ds.DatasetName}</i></> : <> a dataset</>}, then writes a summary CSV the
                    dashboard surfaces as a downloadable artifact:
                </Typography>
                <Typography variant='body1' paragraph>
                    <b>Pattern:</b>
                    <ol style={{ marginTop: 4, marginBottom: 4 }}>
                        <li><code>pick-files</code> — calls <code>dataportalclient.fromDataset(...).listFiles()</code>, takes
                            the first <code>limit</code> files (default 10), and emits a JSON array
                            of <code>{`{id, name}`}</code> objects on stdout. Argo captures it as the
                            template&apos;s <code>files</code> output parameter.</li>
                        <li><code>count-one</code> — Argo&apos;s <code>withParam</code> spawns one pod per array element. Each pod
                            uses <code>dataportalclient.getData(generator=True)</code> to stream the file as 50,000-row pandas
                            DataFrames and sums <code>len(chunk)</code>, never holding the whole file in memory. Output: a
                            single-row JSON object per pod.</li>
                        <li><code>reduce</code> — receives all per-pod results aggregated by Argo into a single JSON array (per-task
                            <code> aggregate</code>), writes <code>summary.csv</code> as an output <i>artifact</i> (uploaded to
                            the cluster artifact store, downloadable from the dashboard).</li>
                    </ol>
                </Typography>
                <Code language='yaml' code={sampleProcessWorkflow(ds)} />
                <Typography variant='body2' color='text.secondary' paragraph sx={{ mt: 1 }}>
                    <b>Concurrency:</b> Kueue admits as many <code>count-one</code> pods as fit in your tenant quota (200 pods, 64 CPU, 256 GiB
                    memory, 8 GPUs). Beyond that, pods queue. Set <code>spec.parallelism</code> on the Workflow to throttle on top of that
                    (e.g. cap to 5 in-flight pods regardless of free quota — useful when each pod hits a rate-limited downstream).
                    For very large fan-outs, fan out twice: file × chunk, where the inner fan-out splits each file into row-ranges and
                    counts them in parallel.
                </Typography>
                <Typography variant='body2' color='text.secondary' paragraph>
                    Both <code>pick-files</code> and <code>count-one</code> use the same <code>dataportalclient</code> the JupyterHub
                    tab teaches — <code>fromDataset(...).listFiles()</code> for listing, <code>getData(generator=True)</code> for
                    streaming file contents in pandas-DataFrame chunks. Authentication is the same Bearer JWT in both places, with
                    full audit-log coverage.
                </Typography>
            </Foldable>

            <Foldable header="MQTT-triggered workflows">
                <Alert severity="info" sx={{ mb: 2 }}>
                    MQTT/file-upload triggers are not yet wired up by the platform. The samples below show the planned contract; until the platform team enables the Argo Events ingester webhook, only manual <code>argo submit</code> and <code>CronWorkflow</code> triggers will work.
                </Alert>
                <Typography variant='body1' paragraph>
                    The WCIB MQTT ingester already batches incoming messages by memory / size / duration thresholds (see <i>MQTT</i> tab — the
                    ingester accumulates messages until ~2 GB / 500 MB compressed / 24 h, whichever comes first). When a batch is committed to a
                    dataset the ingester is planned to fire a webhook into Argo Events; from there your <code>WorkflowTemplate</code> will run <i>per batch</i>,
                    authenticated as <i>you</i> via the <code>DATAPORTAL_CLIENT_TOKEN</code> env variable in your workflow.
                </Typography>

                <Typography variant='body1' paragraph>
                    The pipeline crosses three trust boundaries — knowing where each one is matters when you need to debug auth or audit a
                    run-after-the-fact:
                </Typography>
                <Code language='bash' code={`  MQTT broker  --[ messages ]-->  WCIB ingester (platform pod)
                                          |
                                          | (batch threshold hit; file uploaded to dataset)
                                          v
                                  POST  http://argo-events.../batch-uploaded
                                  body  { event, datasetId, fileId, user, timestamp }
                                  hdr   X-WCIB-Signature: sha256=<HMAC of body>
                                          |
                                          | [trust hop 1: HMAC verifies the request came from
                                          |  the WCIB ingester, not a malicious in-cluster pod]
                                          v
                              EventSource (webhook) in ${eventsNamespace} ns
                                          |
                                          v
                                Sensor reads body fields:
                                  body.user      -> tenant namespace routing
                                  body.datasetId -> Workflow parameter
                                  body.fileId    -> Workflow parameter
                                          |
                                          | [trust hop 2: Sensor's ServiceAccount has
                                          |  permission to 'create workflows' in tenant nses]
                                          v
                            Submit Workflow to ${tenantNsPrefix}<user>
                                          |
                                          v
                               Pod runs as 'argo-batch-executor' SA
                               Uses DATAPORTAL_CLIENT_TOKEN from workflow env
                                          |
                                          | [trust hop 3: WCIB API checks the JWT signature
                                          |  + Redis revocation blacklist on every call]
                                          v
                          GET /v1/dataset/<id>/files/<fid>/content as Bearer
                              audit log: tokenName=argo-job-<wf>, user=<user>
`} />

                <Typography variant='body1' paragraph>
                    <b>Who provides what:</b>
                    <ul style={{ marginTop: 4, marginBottom: 4 }}>
                        <li><b>Platform team</b> (WCIB ops): the MQTT ingester webhook, the Argo Events <code>EventSource</code> + <code>Sensor</code>
                            in the <code>{eventsNamespace}</code> namespace, the HMAC shared-secret pair, and the routing rule that maps
                            <code> body.user</code> to the right tenant namespace.</li>
                        <li><b>You</b>: a <code>WorkflowTemplate</code> in your tenant namespace whose name the Sensor invokes (here:
                            <code> on-mqtt-batch</code>), and a WCIB token (generated in the portal frontend on the <i>Jupyterhub</i> tab via
                            the <i>Generate Token</i> button) set as
                            <code> DATAPORTAL_CLIENT_TOKEN</code> in your workflow&apos;s env, scoped to the datasets you want the triggered workflow to read or write.</li>
                    </ul>
                </Typography>

                <Typography variant='body1' paragraph>
                    <b>The auth chain in detail:</b>
                </Typography>
                <Typography variant='body1' paragraph>
                    <ol style={{ marginTop: 4, marginBottom: 4 }}>
                        <li><b>Ingester &rarr; Argo Events:</b> a shared HMAC secret. The ingester signs each webhook body with HMAC-SHA256 and sends
                            the signature in <code>X-WCIB-Signature</code>; the Argo Events webhook EventSource is configured with the same secret
                            and rejects mismatched signatures. <i>This is the only point at which platform identity is presented — there is no user
                                token in flight here.</i></li>
                        <li><b>Argo Events &rarr; Workflow controller:</b> standard k8s RBAC. The Sensor&apos;s ServiceAccount has cluster-wide permission
                            to create <code>workflows.argoproj.io</code> CRs in any tenant namespace. <i>This is privileged — it&apos;s why the
                                <code> {eventsNamespace}</code> namespace is platform-team-only and not visible to users.</i></li>
                        <li><b>Workflow pod &rarr; WCIB API:</b> your JWT, the same one any other consumer would use. Set as
                            <code> DATAPORTAL_CLIENT_TOKEN</code> in the workflow&apos;s env (generated in the portal frontend on the <i>Jupyterhub</i> tab via
                            the <i>Generate Token</i> button — do not commit it to git).
                            The WCIB API verifies the JWT signature, checks the Redis
                            revocation blacklist on every call, and writes an audit-log entry tagged with the <code>tokenName</code> (Argo-side
                            convention: <code>argo-job-{`{`}{`{`}workflow.name{`}`}{`}`}</code>) and the user the token belongs to. <i>Revoking
                                the token immediately stops in-flight workflows from reading further data — the next API call returns 401.</i></li>
                    </ol>
                </Typography>

                <Typography variant='body1' paragraph>
                    <b>1. The Sensor</b> the platform team deploys (you don&apos;t deploy this — shown so the routing contract is explicit):
                </Typography>
                <Code language='yaml' code={sampleMqttSensor} />

                <Typography variant='body1' paragraph sx={{ mt: 2 }}>
                    <b>2. The webhook payload</b> the WCIB ingester sends. The Sensor&apos;s <code>parameters[].dataKey</code> bindings reference the
                    JSON paths shown here:
                </Typography>
                <Code language='python' code={sampleIngesterWebhook} />

                <Typography variant='body1' paragraph sx={{ mt: 2 }}>
                    <b>3. The <code>WorkflowTemplate</code> you provide</b> in your tenant namespace. The Sensor instantiates it once per MQTT batch,
                    injecting <code>dataset_id</code> + <code>file_id</code> from the event payload:
                </Typography>
                <Code language='yaml' code={sampleMqttWorkflowTemplate} />
                <Typography variant='body2' color='text.secondary' paragraph sx={{ mt: 1 }}>
                    Apply once with <code>kubectl -n &lt;your-tenant-ns&gt; apply -f on-mqtt-batch.yaml</code> (or via the dashboard&apos;s
                    <i> Workflow Templates</i> tab). The Sensor will start triggering it on the very next MQTT batch.
                </Typography>
                <Typography variant='body2' color='text.secondary' paragraph>
                    <b>Reusing the fan-out:</b> the template above is intentionally minimal (one pod per batch). For heavier processing, swap
                    <code> entrypoint: process</code> with the <code>count-pipeline</code> from the fan-out example earlier on this page — same DAG,
                    just driven by an MQTT event instead of a manual <code>argo submit</code>.
                </Typography>
            </Foldable>

            <Foldable header="Scheduled workflows">
                <Typography variant='body1' paragraph>
                    Use <code>CronWorkflow</code> for periodic jobs (weekly reports, nightly aggregations, scheduled health checks).
                    A <code>CronWorkflow</code> is a Workflow definition + a cron schedule + a timezone — Argo creates a fresh
                    Workflow from the embedded <code>workflowSpec</code> on every tick. The schedule is the standard 5-field cron
                    syntax (<code>minute hour day-of-month month day-of-week</code>); the timezone is an IANA zone name
                    (<code>Europe/Stockholm</code> here, which auto-handles CET/CEST). Apply once with <code>kubectl apply</code> — the
                    schedule keeps running until you delete the CR.
                </Typography>
                <Code language='yaml' code={sampleCronWorkflow} />
            </Foldable>

            <Typography variant='h6' sx={{ mt: 3 }} gutterBottom>Egress</Typography>
            <Typography variant='body1' paragraph>
                <b>Internet access:</b> tenant pods can reach the WCIB API and DNS by default — purely-internal
                steps (e.g. <code>dataportalclient</code> calls against the in-cluster API) need no special
                configuration. Need <code>pip install</code> from PyPI/GitHub or external APIs anywhere in the
                workflow? Add <code> argo-batch.wcib/internet-egress: &quot;true&quot;</code> to
                <code> spec.podMetadata.labels</code> at the workflow level (the YAML examples above do this).
                Argo applies <code>spec.podMetadata</code> to every pod the workflow spawns, so one declaration
                covers all steps.
            </Typography>

            <Typography variant='h6' sx={{ mt: 3 }} gutterBottom>Updates</Typography>
            <Typography variant='body1' paragraph>
                Argo Workflows, Argo Events and Kueue are updated regularly. <Abbr title="Custom Resource Definition">CRD</Abbr>-level changes are coordinated by the platform team and announced
                on the WCIB status page. Your workflows survive upgrades; in-flight runs are not interrupted.
            </Typography>
        </Card>

        {renderArgoButton()}

        <Card className={styles.expcard} sx={{ mt: 2 }}>
            <Typography variant='h6' gutterBottom>Documentation &amp; references</Typography>
            <Typography variant='body2' color='text.secondary' paragraph>
                Argo and Kueue have excellent upstream docs. Start with the Workflows core concepts and the Argo Server SSO page; come back to
                Argo Events when you&apos;re ready to wire up MQTT or webhook triggers.
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
                <Link href="https://argo-workflows.readthedocs.io/en/stable/" target='_blank' rel='noopener noreferrer'>
                    Argo Workflows — official docs
                </Link>
                <Link href="https://argo-workflows.readthedocs.io/en/stable/walk-through/" target='_blank' rel='noopener noreferrer'>
                    Argo Workflows — walk-through tutorial
                </Link>
                <Link href="https://argo-workflows.readthedocs.io/en/stable/cron-workflows/" target='_blank' rel='noopener noreferrer'>
                    Argo Workflows — CronWorkflow reference
                </Link>
                <Link href="https://argo-workflows.readthedocs.io/en/stable/argo-server-sso/" target='_blank' rel='noopener noreferrer'>
                    Argo Server SSO — how login &amp; namespace mapping work
                </Link>
                <Link href="https://argoproj.github.io/argo-events/" target='_blank' rel='noopener noreferrer'>
                    Argo Events — EventSources, Sensors, triggers
                </Link>
                <Link href="https://argoproj.github.io/argo-events/eventsources/setup/mqtt/" target='_blank' rel='noopener noreferrer'>
                    Argo Events — MQTT EventSource
                </Link>
                <Link href="https://kueue.sigs.k8s.io/docs/" target='_blank' rel='noopener noreferrer'>
                    Kueue — fair-share &amp; preemption concepts
                </Link>
                <Link href="https://github.com/argoproj-labs/hera" target='_blank' rel='noopener noreferrer'>
                    Hera — Python SDK for writing Workflows in Python instead of YAML
                </Link>
            </Stack>
        </Card>

        <Snackbar open={!!errorMsg} autoHideDuration={6000} onClose={() => setErrorMsg('')}>
            <Alert onClose={() => setErrorMsg('')} severity="error" sx={{ width: '100%' }}>
                {errorMsg}
            </Alert>
        </Snackbar>
    </>;
}
